import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { baseurl } from '../Baseurl';
import { parseISO, isWithinInterval } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const timezones = [
  { label: 'IST (India)', value: 'Asia/Kolkata' },
  { label: 'UTC', value: 'UTC' },
  { label: 'EST (New York)', value: 'America/New_York' },
  { label: 'PST (California)', value: 'America/Los_Angeles' },
  { label: 'CET (Europe)', value: 'Europe/Berlin' },
  { label: 'JST (Japan)', value: 'Asia/Tokyo' },
];

export default function UserActivityLog() {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [timezone, setTimezone] = useState(localStorage.getItem('log_timezone') || 'Asia/Kolkata');
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [page, setPage] = useState(1);
  const logsPerPage = 10;
  const adminID = localStorage.getItem('adminID');

  useEffect(() => {
    if (adminID) {
      axios.get(`${baseurl}getLogsByUserId/${adminID}`).then((res) => {
        if (res.data.status === 'success') {
          setLogs(res.data.logs);
          setFilteredLogs(res.data.logs);
        }
      });
    }
  }, [adminID]);

  useEffect(() => {
    filterLogs();
  }, [search, startDate, endDate, timezone]);

  const filterLogs = () => {
    let result = logs;

    if (search) {
      result = result.filter((log) =>
        Object.values(log).some(
          (val) => typeof val === 'string' && val.toLowerCase().includes(search.toLowerCase())
        )
      );
    }

    if (startDate && endDate) {
      result = result.filter((log) =>
        isWithinInterval(parseISO(log.timestamp), {
          start: startDate,
          end: endDate,
        })
      );
    }

    setFilteredLogs(result);
    setPage(1); // reset page after filtering
  };

  const handleTimezoneChange = (e) => {
    setTimezone(e.target.value);
    localStorage.setItem('log_timezone', e.target.value);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', { timeZone: timezone });
  };

  const exportCSV = () => {
    const headers = ['#', 'Timestamp', 'Module', 'Action', 'Description', 'IP', 'Device'];
    const rows = filteredLogs.map((log, index) => [
      index + 1,
      formatDate(log.timestamp),
      log.module,
      log.action_type.toUpperCase(),
      log.description?.replace(/[\n\r]+/g, ' '),
      log.ip_address,
      log.user_agent?.split(')')[0].replace('(', ''),
    ]);

    const csvContent =
      [headers.join(','), ...rows.map((r) => r.map((x) => `"${x}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `activity_logs_${Date.now()}.csv`;
    link.click();
  };

  const paginatedLogs = filteredLogs.slice((page - 1) * logsPerPage, page * logsPerPage);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="fw-bold">🕵️‍♂️ User Activity Logs</h3>
        <div className="d-flex gap-2">
          <button className="btn new-project-btn tab4 text-white btn-sm" onClick={exportCSV}>
            Export
          </button>
          <select className="form-select form-select-sm" value={timezone} onChange={handleTimezoneChange}>
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search logs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable
            placeholderText="📅 Filter by date range"
            className="form-control"
          />
        </div>
      </div>

      <div className="table-responsive shadow-sm border rounded">
        <table className="custom-table">
          <thead className=" ">
            <tr>
              <th>#</th>
              <th>Timestamp</th>
              <th>Module</th>
              <th>Action</th>
              <th>Description</th>
              <th>IP Address</th>
              <th>Device</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLogs.length > 0 ? (
              paginatedLogs.map((log, index) => (
                <tr key={log.id}>
                  <td>{(page - 1) * logsPerPage + index + 1}</td>
                  <td>{formatDate(log.timestamp)}</td>
                  <td><span className="badge tab5">{log.module}</span></td>
                  <td>{log.action_type.toUpperCase()}</td>
                  <td className="text-wrap" style={{ maxWidth: 300 }}>{log.description}</td>
                  <td>{log.ip_address}</td>
                  <td className="text-wrap" style={{ maxWidth: 250 }}>
                    {log.user_agent?.split(')')[0].replace('(', '')}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="text-center text-muted py-3">
                  No logs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-end mt-3">
          <nav>
            <ul className="pagination pagination-sm mb-0">
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}
